import { useState, useEffect } from "react";
import { Loader2, Scissors, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { useWorkspace } from "../../store/WorkspaceContext";
import { calibrationCommands, bladeTestCommands, sendViaTransport, DEFAULT_CONNECTION } from "../../lib/transport";
import { DirectionTest } from "./DirectionTest";
import { Compass } from "lucide-react";

export const FILM_WIDTH_OPTIONS = [8, 12, 16, 20, 22, 24, 30, 32, 36, 38, 40, 44, 48, 50, 52, 54, 58, 60];

export const BladeIcon = ({ className = "h-4 w-4" }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <path d="M3 17h18" /><path d="M3 20h18" strokeDasharray="2 2" />
    <path d="M13 3l6 6-6 8-3-3z" /><path d="M13 3 10 14" />
    <path d="M6 8v6M4.5 12.5 6 14l1.5-1.5" />
  </svg>
);

const SQUARE_MM = 254;
const Num = ({ id, value, onChange, unit, className = "" }) => (
  <span className={`flex items-center gap-1.5 ${className}`}><input data-testid={id} type="number" inputMode="decimal" step="0.1" min="0" value={value} onChange={(e) => onChange(e.target.value)} onFocus={(e) => e.target.select()} className="field h-11 w-36 text-[16px] font-mono" /><span className="text-[13px] text-[#9aa0a8]">{unit}</span></span>
);
const pct = (v) => `${(v * 100).toFixed(2)}%`;

export const CalibrationDialog = () => {
  const { state, dispatch } = useWorkspace();
  const open = state.dialog === "calibration" || state.dialog === "direction";
  const set = (patch) => dispatch({ type: "SET", patch });
  const c = { ...DEFAULT_CONNECTION, ...state.connection };
  const fmt = state.machine || "hpgl";
  const [view, setView] = useState(state.dialog === "direction" ? "direction" : "blade");
  const [lastDialog, setLastDialog] = useState(state.dialog);
  if (open && state.dialog !== lastDialog) { setLastDialog(state.dialog); setView(state.dialog === "direction" ? "direction" : "blade"); }
  const [bladeWidth, setBladeWidth] = useState(36);
  const [actualW, setActualW] = useState(String(SQUARE_MM));
  const [actualH, setActualH] = useState(String(SQUARE_MM));
  useEffect(() => { if (open) { setActualW(String(SQUARE_MM)); setActualH(String(SQUARE_MM)); } }, [open]); // boxes start at 254 each time; typed values stay put until the dialog is closed
  const [busy, setBusy] = useState(false);
  const cal = state.calibration || { scale_width: 1, scale_length: 1 };
  // DigiCut-style absolute calibration: the test square is cut unscaled, scale = nominal / measured (261 mm → 97.32 %, 250 mm → 101.6 %)
  const scaleW = +actualW > 0 ? SQUARE_MM / +actualW : 1;
  const scaleL = +actualH > 0 ? SQUARE_MM / +actualH : 1;
  const RAW = { scale_width: 1, scale_length: 1 };

  const send = async (commands, what) => {
    if (!state.machine) return toast.error("No plotter profile yet — open Setting → Plotter and select your plotter");
    if (c.transport === "download") return toast.error("Plotter is set to \"Cut file only\" — open Setting → Plotter and select your plotter first");
    if (c.transport === "bridge" && !state.bridge) return toast.error("Shop computer is offline — start the Xpattern connector on the PC the plotter is plugged into");
    setBusy(true);
    try { const d = await sendViaTransport(commands, c); toast.success(`${what} sent (${d})`); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const save = () => {
    if (scaleW < 0.8 || scaleW > 1.25 || scaleL < 0.8 || scaleL > 1.25) return toast.error(`Measurement out of range — the square is ${SQUARE_MM} mm, please re-measure`);
    const changes = Math.abs(scaleW - 1) > 0.0005 || Math.abs(scaleL - 1) > 0.0005;
    // Patterns always cut at 100% true size; only a real measured square may change that — so confirm before scaling every future cut.
    if (changes && !window.confirm(`This will scale EVERY pattern you cut from now on to ${pct(scaleW)} wide × ${pct(scaleL)} long.\n\nOnly continue if you just cut the ${SQUARE_MM} mm square and measured it with a ruler.\n\nApply this calibration?`)) return;
    const next = { ...cal, scale_width: +scaleW.toFixed(5), scale_length: +scaleL.toFixed(5), measured_w: +actualW, measured_h: +actualH };
    set({ calibration: next });
    api.saveCalibration(next).catch(() => {});  // account-wide: every pattern on every computer of this account is corrected
    toast.success(`Calibration saved · width ${pct(next.scale_width)} · length ${pct(next.scale_length)} · applied to every pattern you cut`);
  };
  const saveDirections = (d) => {
    const next = { ...cal, ...d };
    set({ calibration: next, dialog: null });
    api.saveCalibration(next).catch(() => {});
  };
  const reset = () => {
    const next = { ...cal, scale_width: 1, scale_length: 1, measured_w: SQUARE_MM, measured_h: SQUARE_MM };
    set({ calibration: next }); setActualW(String(SQUARE_MM)); setActualH(String(SQUARE_MM));
    api.saveCalibration(next).catch(() => {});
    toast.info("Calibration reset to 100%");
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && set({ dialog: null })}>
      <DialogContent className={`panel3d ${view === "plotter" ? "max-w-2xl" : "max-w-lg"} text-[#e8e9ec]`} data-testid="calibration-dialog">
        {view === "direction" ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[15px]"><Compass className="h-4 w-4 text-[#2f80ff]" /> Plotter Direction Test</DialogTitle>
              <DialogDescription className="text-[#9aa0a8]">Load film, set the origin on the plotter as you normally do, then answer three quick questions. Xpattern learns which way your plotter feeds and cuts accordingly.</DialogDescription>
            </DialogHeader>
            <DirectionTest cal={cal} fmt={fmt} send={send} busy={busy} onSave={saveDirections} />
            <div className="flex justify-end pt-2"><button data-testid="to-blade-calibration-btn" className="btn" onClick={() => setView("blade")}>Blade Calibration</button></div>
          </>
        ) : view === "blade" ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-[15px]"><BladeIcon className="h-4 w-4 text-[#2f80ff]" /> Blade Depth Calibration</DialogTitle>
              <DialogDescription className="text-[#9aa0a8]">Cuts a small square. Peel it: the blade should cut the film cleanly but only scratch the liner. Adjust blade depth on the plotter and cut again.</DialogDescription>
            </DialogHeader>
            <div className="flex items-center justify-center gap-3 py-4" data-testid="blade-form">
              <span className="text-[13px] text-[#9aa0a8]">Width</span>
              <input data-testid="blade-width-select" type="number" list="film-width-options" min="1" max="60" step="0.5" value={bladeWidth} onChange={(e) => setBladeWidth(+e.target.value)} className="field w-32 font-mono" placeholder="Film width" />
              <datalist id="film-width-options">{FILM_WIDTH_OPTIONS.map((w) => <option key={w} value={w} />)}</datalist>
              <span className="text-[12px] text-[#9aa0a8]">in</span>
            </div>
            <p className="text-center text-[11px] text-[#6b7079]">Cuts a 1" test square in the middle of a {bladeWidth}" film so it lands on the material.</p>
            <div className="flex justify-center">
              <button data-testid="blade-cut-btn" className="btn-red px-10" disabled={busy || !(bladeWidth > 0)} onClick={() => send(bladeTestCommands(fmt, bladeWidth, cal), "Blade test square")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} Cut</button>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <button data-testid="to-direction-test-btn" className="btn" onClick={() => setView("direction")}><Compass className="h-3.5 w-3.5" /> Direction Test</button>
              <button data-testid="to-plotter-calibration-btn" className="btn" onClick={() => setView("plotter")}>Calibrate Plotter</button>
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="text-[15px]">Plotter Calibration</DialogTitle>
              <DialogDescription className="text-[#9aa0a8]">Cut the {SQUARE_MM} mm (10") square, measure it and type the real size. Bigger than {SQUARE_MM} mm → scale below 100%, smaller → above 100%. Every pattern you cut is corrected by these factors.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-[150px_1fr] gap-6" data-testid="plotter-calibration-form">
              <div className="flex flex-col justify-center space-y-2">
                <div className="text-[12px] text-[#9aa0a8]">Enter Actual Height</div>
                <Num id="actual-height-input" value={actualH} onChange={setActualH} unit="mm" />
                <div className="text-[11px] text-[#9aa0a8]">Scaled <span className="font-mono text-[#e8e9ec]" data-testid="scaled-length">{pct(scaleL)}</span></div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-3 text-[12px] text-[#9aa0a8]">Enter Actual Width <Num id="actual-width-input" value={actualW} onChange={setActualW} unit="mm" /></div>
                <div className="text-center text-[11px] text-[#9aa0a8]">Scaled <span className="font-mono text-[#e8e9ec]" data-testid="scaled-width">{pct(scaleW)}</span></div>
                <div className="mx-auto grid w-fit grid-cols-[auto_auto] grid-rows-[auto_auto] items-center gap-2 pt-1">
                  <div className="col-start-1 row-start-1 flex items-center justify-center gap-2 text-[12px] text-[#c7cbd1]"><span className="h-px w-10 bg-[#4a5673]" />{SQUARE_MM} millimeters<span className="h-px w-10 bg-[#4a5673]" /></div>
                  <div className="col-start-1 row-start-2 h-64 w-64 border-2 border-[#d0d3d8]" data-testid="calibration-square" />
                  <div className="col-start-2 row-start-2 flex h-64 items-center"><span className="whitespace-nowrap text-[12px] text-[#c7cbd1] [writing-mode:vertical-rl]">{SQUARE_MM} millimeters</span></div>
                </div>
              </div>
            </div>
            <div className="text-center text-[11px] text-[#6b7079]" data-testid="current-calibration">Current: width {pct(cal.scale_width)} · length {pct(cal.scale_length)}{cal.measured_w ? ` · last measured ${cal.measured_w} × ${cal.measured_h} mm` : ""}</div>
            <div className="grid grid-cols-3 gap-2 pt-2">
              <button data-testid="calibration-reset-btn" className="btn" onClick={reset}><RotateCcw className="h-3.5 w-3.5" /> Reset</button>
              <button data-testid="calibration-save-btn" className="btn" onClick={save} disabled={!(+actualW > 0) || !(+actualH > 0)}><Save className="h-3.5 w-3.5" /> Save</button>
              <button data-testid="calibration-cut-btn" className="btn-red" disabled={busy} onClick={() => send(calibrationCommands(fmt, 10, 10, RAW), "10\" calibration square")}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Scissors className="h-4 w-4" />} Cut</button>
            </div>
            <div className="flex justify-end pt-2">
              <button data-testid="to-blade-calibration-btn" className="btn" onClick={() => setView("blade")}>Blade Calibration</button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
